import React, { useState, useEffect, Fragment } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Layout from "../../components/global/layout";

export default function QuickBookItemList() {
  const user = useSelector((state) => state.user);
  const history = useNavigate();

  const isConnected = !!user?.quick_book_accesToken;

  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "active" | "inactive"
  const [typeFilter, setTypeFilter] = useState(""); // "" | unique Type values
  const [selectedItem, setSelectedItem] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // ── Fetch items ──
  const fetchItems = async () => {
    loader(true);
    await ApiClient.post("quickbook/items", {
      quickbook_token: user?.quick_book_accesToken || "",
      company: user?.quick_book_companyId,
    })
      .then((res) => {
        if (res?.success) setItems(res?.data || []);
      })
      .catch(console.error);
    loader(false);
  };

  useEffect(() => {
    if (isConnected) fetchItems();
  }, []);

  // ── Unique types for filter pills ──
  const uniqueTypes = [...new Set(items.map((i) => i.Type).filter(Boolean))];

  // ── Filtered list ──
  const filteredItems = items.filter((item) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      item.Name?.toLowerCase().includes(q) ||
      item.Description?.toLowerCase().includes(q) ||
      item.Type?.toLowerCase().includes(q) ||
      item.Id?.toString().includes(q) ||
      item.IncomeAccountRef?.name?.toLowerCase().includes(q);

    const matchesStatus =
      statusFilter === ""
        ? true
        : statusFilter === "active"
          ? item.Active === true
          : item.Active === false;

    const matchesType = typeFilter === "" ? true : item.Type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  // ── Derived stats ──
  const activeCount = items.filter((i) => i.Active).length;
  const taxableCount = items.filter((i) => i.Taxable).length;
  const trackedCount = items.filter((i) => i.TrackQtyOnHand).length;

  // ── Helpers ──
  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(val ?? 0);

  const formatDate = (str) => {
    if (!str) return "—";
    const d = new Date(str);
    if (isNaN(d)) return "—";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (!parts.length) return "?";
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  // ── Type color map ──
  const typeColor = (type) => {
    const map = {
      Service: {
        bg: "bg-[#115B9D]/10",
        text: "text-[#115B9D]",
        border: "border-[#115B9D]/20",
      },
      Inventory: {
        bg: "bg-purple-50",
        text: "text-purple-700",
        border: "border-purple-200",
      },
      NonInventory: {
        bg: "bg-orange-50",
        text: "text-orange-700",
        border: "border-orange-200",
      },
    };
    return (
      map[type] || {
        bg: "bg-gray-100",
        text: "text-gray-600",
        border: "border-gray-200",
      }
    );
  };

  // ── Badges ──
  const TypeBadge = ({ type }) => {
    const c = typeColor(type);
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${c.bg} ${c.text} ${c.border}`}
      >
        {type || "—"}
      </span>
    );
  };

  const StatusBadge = ({ active }) => (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${
        active
          ? "bg-[#F0FEF4] text-[#008236] border-[#0a7822a6] border"
          : "bg-gray-100 text-gray-500 border border-gray-200"
      }`}
    >
      <span
        className={`size-1.5 rounded-full ${active ? "bg-[#0a7822a6]" : "bg-gray-400"}`}
      />
      {active ? "Active" : "Inactive"}
    </span>
  );

  const STATUS_TABS = [
    { label: "All", value: "" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const closeModal = () => {
    setIsViewModalOpen(false);
    setSelectedItem(null);
  };

  // ── Not Connected ──
  if (!isConnected) {
    return (
      <Layout>
        <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
          <div className="flex flex-col gap-1 mb-6">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
              Products & Services
            </h2>
            <p className="text-sm sm:text-md text-gray-600">
              Manage your QuickBooks items
            </p>
          </div>

          <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
            <div className="flex flex-col items-center justify-center py-20 gap-6 text-center">
              <div className="size-20 rounded-2xl bg-[#2CA01C]/10 border border-[#2CA01C]/20 flex items-center justify-center">
                <svg
                  viewBox="0 0 40 40"
                  width="40"
                  height="40"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="18"
                    cy="18"
                    r="10"
                    fill="none"
                    stroke="#2CA01C"
                    strokeWidth="3"
                  />
                  <line
                    x1="25"
                    y1="25"
                    x2="31"
                    y2="31"
                    stroke="#2CA01C"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="space-y-1.5">
                <h3 className="text-lg font-semibold text-gray-900">
                  QuickBooks Not Connected
                </h3>
                <p className="text-sm text-gray-500 max-w-sm">
                  Connect your QuickBooks account to view and manage your
                  products & services.
                </p>
              </div>
              <button
                type="button"
                onClick={() => history("/quickbook/integration")}
                className="w-fit inline-flex items-center gap-2 bg-[#115B9D] hover:opacity-80 text-white text-sm rounded-xl transition-colors font-medium px-5 py-2.5"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
                Connect QuickBooks
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Main Listing ──
  return (
    <Layout>
      <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
        {/* ─── Page Header ─── */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <div className="flex flex-col gap-1">
            <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
              Products & Services
            </h2>
            <p className="text-sm sm:text-md text-gray-600">
              {items.length} item{items.length !== 1 ? "s" : ""} synced
              {trackedCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md bg-[#115B9D]/10 text-[#115B9D] text-xs font-semibold">
                  {trackedCount} qty tracked
                </span>
              )}
            </p>
          </div>

          {/* QB Connected badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F0FEF4] border border-[#008236]/20 rounded-xl">
            <span className="size-2 rounded-full bg-[#008236] animate-pulse" />
            <span className="text-xs font-medium text-[#008236]">
              QuickBooks Connected
            </span>
          </div>
        </div>

        {/* ─── Summary Cards ─── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {[
            {
              label: "Total Items",
              value: items.length,
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#115B9D]"
                >
                  <path d="m7.5 4.27 9 5.15" />
                  <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                  <path d="m3.3 7 8.7 5 8.7-5" />
                  <path d="M12 22V12" />
                </svg>
              ),
              bg: "bg-[#115B9D17]",
            },
            {
              label: "Active Items",
              value: activeCount,
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#008236]"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              ),
              bg: "bg-[#f0fdf4]",
            },
            {
              label: "Taxable Items",
              value: taxableCount,
              icon: (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-amber-600"
                >
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              ),
              bg: "bg-amber-50",
            },
          ].map((card) => (
            <div
              key={card.label}
              className="border border-gray-200 rounded-2xl bg-white px-5 py-4 flex items-center gap-4"
            >
              <div
                className={`size-10 rounded-xl ${card.bg} flex items-center justify-center shrink-0`}
              >
                {card.icon}
              </div>
              <div>
                <p className="text-xs text-gray-600 font-medium">
                  {card.label}
                </p>
                <p className="text-xl font-semibold text-gray-900 mt-0.5">
                  {card.value}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="w-full border rounded-2xl bg-white p-5 lg:p-8">
          {/* ─── Filters Row ─── */}
          <div className="flex flex-col gap-3 mb-5">
            <div className="flex flex-wrap items-start sm:items-center justify-between gap-3">
              {/* Status Tabs */}
              <div className="max-sm:w-full overflow-auto w-full sm:w-fit overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl p-1.5">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setStatusFilter(tab.value)}
                    className={`whitespace-nowrap px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      statusFilter === tab.value
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="relative w-full sm:w-[350px]">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, type, ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
                />
              </div>
            </div>

            {/* Type filter pills */}
            {uniqueTypes.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-600 font-medium">Type:</span>
                <button
                  type="button"
                  onClick={() => setTypeFilter("")}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                    typeFilter === ""
                      ? "bg-[#115B9D] text-white border-[#115B9D]"
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  All
                </button>
                {uniqueTypes.map((type) => {
                  const c = typeColor(type);
                  const isActive = typeFilter === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setTypeFilter(isActive ? "" : type)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium border transition-all ${
                        isActive
                          ? `${c.bg} ${c.text} ${c.border}`
                          : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ─── Table ─── */}

          {/* Desktop Table */}
<div className="hidden md:block overflow-x-auto xl:overflow-hidden border border-gray-200 rounded-xl scrollbar">
  <table className="w-full min-w-[1000px] xl:min-w-0 table-auto xl:table-fixed">
    <colgroup>
  <col style={{ width: "6%" }} /> {/* Actions */}
  <col style={{ width: "8%" }} /> {/* ID */}
  <col style={{ width: "11%" }} /> {/* Item */}
  <col style={{ width: "17%" }} /> {/* Description */}
  <col style={{ width: "10%" }} /> {/* Type */}
  <col style={{ width: "9%" }} /> {/* Unit Price */}
  <col style={{ width: "11%" }} /> {/* Purchase Cost */}
  <col style={{ width: "7%" }} /> {/* Taxable */}
  <col style={{ width: "8%" }} /> {/* Status */}
  <col style={{ width: "13%" }} /> {/* Last Updated */}
</colgroup>
    <thead className="bg-gray-50 border-b border-gray-200">
      <tr>
        {[
          "Actions",
          "ID",
          "Item",
          "Description",
          "Type",
          "Unit Price",
          "Purchase Cost",
          "Taxable",
          "Status",
          "Last Updated",
        ].map((h) => (
          <th key={h} className="text-left py-3 px-3 align-bottom">
            <span className="text-xs text-gray-700 font-medium whitespace-normal leading-tight block">
              {h}
            </span>
          </th>
        ))}
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-100">
      {filteredItems.length === 0 ? (
        <tr>
          <td colSpan={10} className="text-center py-16">
            <div className="flex flex-col items-center gap-3">
              <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-gray-600"
                >
                  <path d="m7.5 4.27 9 5.15" />
                  <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                  <path d="m3.3 7 8.7 5 8.7-5" />
                  <path d="M12 22V12" />
                </svg>
              </div>
              <p className="text-sm text-gray-500">No items found</p>
            </div>
          </td>
        </tr>
      ) : (
        filteredItems.map((item, index) => (
          <tr
            key={item.Id || index}
            className="hover:bg-gray-50/60 transition-colors align-middle"
          >
            {/* Actions */}
            <td className="py-2.5 px-3 align-middle">
              <button
                type="button"
                onClick={() => {
                  setSelectedItem(item);
                  setIsViewModalOpen(true);
                }}
                title="View Details"
                className="size-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </td>

            {/* ID */}
            <td className="py-2.5 px-3 align-middle overflow-hidden">
              <span
                className="inline-block px-2 py-1 rounded-lg bg-gray-100 border border-gray-200 text-[11px] font-semibold text-gray-600 whitespace-nowrap"
                title={`#${item.Id}`}
              >
                #{item.Id}
              </span>
            </td>

            {/* Item Name */}
            <td className="py-2.5 px-3 align-middle overflow-hidden">
              <div className="flex items-start gap-2 min-w-0">
                <div className="size-8 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-semibold text-[#115B9D]">
                    {getInitials(item.Name)}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-gray-800 break-words leading-tight">
                    {item.Name || "—"}
                  </p>
                  {item.FullyQualifiedName &&
                    item.FullyQualifiedName !== item.Name && (
                      <p className="text-[11px] text-gray-600 break-words leading-tight">
                        {item.FullyQualifiedName}
                      </p>
                    )}
                </div>
              </div>
            </td>

            {/* Description — full show, no truncate */}
            <td className="py-2.5 px-3 align-middle overflow-hidden">
              <p className="text-xs text-gray-500 break-words leading-snug">
                {item.Description || "—"}
              </p>
            </td>

            {/* Type */}
            <td className="py-2.5 px-3 align-middle overflow-hidden">
              <TypeBadge type={item.Type} />
            </td>

            {/* Unit Price */}
            <td className="py-2.5 px-3 align-middle">
              <p className="text-xs font-medium text-gray-800 whitespace-nowrap">
                {formatCurrency(item.UnitPrice)}
              </p>
            </td>

            {/* Purchase Cost */}
            <td className="py-2.5 px-3 align-middle">
              <p className="text-xs text-gray-500 whitespace-nowrap">
                {formatCurrency(item.PurchaseCost)}
              </p>
            </td>

            {/* Taxable */}
            <td className="py-2.5 px-3 align-middle">
              {item.Taxable ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 whitespace-nowrap">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  Yes
                </span>
              ) : (
                <span className="text-xs font-medium text-gray-600">No</span>
              )}
            </td>

            {/* Status */}
            <td className="py-2.5 px-3 align-middle">
              <StatusBadge active={item.Active} />
            </td>

            {/* Last Updated */}
<td className="py-2.5 px-3 align-middle overflow-hidden">
  <p className="text-xs text-gray-700 break-words leading-tight">
    {formatDate(item.MetaData?.LastUpdatedTime)}
  </p>
  <p className="text-[11px] text-gray-600 break-words leading-tight">
    Created {formatDate(item.MetaData?.CreateTime)}
  </p>
</td>
          </tr>
        ))
      )}
    </tbody>
  </table>
</div>

          {/* Mobile Cards */}
          <div className="md:hidden">
            {filteredItems.length === 0 ? (
              <div className="border border-gray-200 rounded-xl py-16 flex flex-col items-center gap-3">
                <div className="size-14 rounded-full bg-gray-100 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-600"
                  >
                    <path d="m7.5 4.27 9 5.15" />
                    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                    <path d="m3.3 7 8.7 5 8.7-5" />
                    <path d="M12 22V12" />
                  </svg>
                </div>
                <p className="text-sm text-gray-500">No items found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredItems.map((item, index) => (
                  <div
                    key={item.Id || index}
                    className="border border-gray-200 rounded-xl p-4 bg-white"
                  >
                    {/* Top row: Item info + View action */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-start gap-2.5 min-w-0">
                        <div className="size-9 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0">
                          <span className="text-xs font-semibold text-[#115B9D]">
                            {getInitials(item.Name)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 break-words">
                            {item.Name || "—"}
                          </p>
                          {item.FullyQualifiedName &&
                            item.FullyQualifiedName !== item.Name && (
                              <p className="text-xs text-gray-600 break-words">
                                {item.FullyQualifiedName}
                              </p>
                            )}
                          <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded-lg bg-gray-100 border border-gray-200 text-[11px] font-semibold text-gray-600 break-all">
                            #{item.Id}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedItem(item);
                          setIsViewModalOpen(true);
                        }}
                        title="View Details"
                        className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </div>

                    {/* Description */}
                    {item.Description && (
                      <div className="py-1.5 border-t border-gray-100">
                        <span className="text-xs text-gray-500 block mb-0.5">
                          Description
                        </span>
                        <p className="text-sm text-gray-500 break-words">
                          {item.Description}
                        </p>
                      </div>
                    )}

                    {/* Type */}
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Type</span>
                      <TypeBadge type={item.Type} />
                    </div>

                    {/* Unit Price */}
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Unit Price</span>
                      <p className="text-sm font-medium text-gray-800">
                        {formatCurrency(item.UnitPrice)}
                      </p>
                    </div>

                    {/* Purchase Cost */}
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">
                        Purchase Cost
                      </span>
                      <p className="text-sm text-gray-500">
                        {formatCurrency(item.PurchaseCost)}
                      </p>
                    </div>

                    {/* Taxable */}
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Taxable</span>
                      {item.Taxable ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                          Yes
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-gray-600">
                          No
                        </span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="flex justify-between items-center py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Status</span>
                      <StatusBadge active={item.Active} />
                    </div>

                    {/* Last Updated */}
                    <div className="flex justify-between items-start py-1.5 border-t border-gray-100">
                      <span className="text-xs text-gray-500 shrink-0">
                        Last Updated
                      </span>
                      <div className="text-right">
                        <p className="text-sm text-gray-700">
                          {formatDate(item.MetaData?.LastUpdatedTime)}
                        </p>
                        <p className="text-xs text-gray-600">
                          Created {formatDate(item.MetaData?.CreateTime)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Item Detail Modal ─── */}
      <Transition appear show={isViewModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[999]" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="min-w-9 w-9 h-9 rounded-xl bg-[#115B9D17] flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-[#115B9D]"
                        >
                          <path d="m7.5 4.27 9 5.15" />
                          <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                          <path d="m3.3 7 8.7 5 8.7-5" />
                          <path d="M12 22V12" />
                        </svg>
                      </div>
                      <div>
                        <Dialog.Title className="text-base font-semibold text-gray-900">
                          Item Details
                        </Dialog.Title>
                        {selectedItem && (
                          <div className="flex items-center gap-2 mt-1">
                            <StatusBadge active={selectedItem.Active} />
                            <TypeBadge type={selectedItem.Type} />
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={closeModal}
                      className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-gray-600 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {selectedItem && (
                    <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5 scrollbar">
                      {/* Item Header Card */}
                      <div className="flex items-center gap-4 p-4 bg-[#115B9D08] border border-[#115B9D]/10 rounded-xl">
                        <div className="size-14 rounded-xl bg-[#115B9D17] border border-[#115B9D]/15 flex items-center justify-center shrink-0">
                          <span className="text-lg font-bold text-[#115B9D]">
                            {getInitials(selectedItem.Name)}
                          </span>
                        </div>
                        <div>
                          <p className="text-base font-semibold text-gray-900">
                            {selectedItem.Name || "—"}
                          </p>
                          {selectedItem.FullyQualifiedName &&
                            selectedItem.FullyQualifiedName !==
                              selectedItem.Name && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                {selectedItem.FullyQualifiedName}
                              </p>
                            )}
                          {selectedItem.Description && (
                            <p className="text-xs text-gray-600 mt-1 leading-snug">
                              {selectedItem.Description}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Core Details */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Item Details
                        </p>
                        <div className="pt-2 grid grid-cols-2 gap-3">
                          {[
                            { label: "Item ID", value: `#${selectedItem.Id}` },
                            {
                              label: "Sync Token",
                              value: selectedItem.SyncToken || "—",
                            },
                            { label: "Type", value: selectedItem.Type || "—" },
                            {
                              label: "Domain",
                              value: selectedItem.domain || "—",
                            },
                            {
                              label: "Track Qty",
                              value: selectedItem.TrackQtyOnHand ? "Yes" : "No",
                            },
                            {
                              label: "Taxable",
                              value: selectedItem.Taxable ? "Yes" : "No",
                            },
                          ].map((d) => (
                            <div
                              key={d.label}
                              className="p-3 border border-gray-100 rounded-xl bg-gray-50/50"
                            >
                              <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">
                                {d.label}
                              </p>
                              <p className="text-sm text-gray-800 mt-0.5 break-words">
                                {d.value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Income Account */}
                      {selectedItem.IncomeAccountRef && (
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                            Income Account
                          </p>
                          <div className="pt-2">
                            <div className="flex items-start gap-3 p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                              <div className="size-7 rounded-lg bg-[#115B9D17] flex items-center justify-center shrink-0 mt-0.5">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="13"
                                  height="13"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="text-[#115B9D]"
                                >
                                  <path d="M3 3h18v18H3z" />
                                  <path d="M3 9h18M3 15h18M9 3v18" />
                                </svg>
                              </div>
                              <div className="min-w-0">
                                <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">
                                  Account Ref #
                                  {selectedItem.IncomeAccountRef.value}
                                </p>
                                <p className="text-sm text-gray-800 mt-0.5 break-words">
                                  {selectedItem.IncomeAccountRef.name}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Pricing */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Pricing
                        </p>
                        <div className="border border-gray-100 rounded-xl overflow-hidden">
                          {[
                            {
                              label: "Unit Price",
                              value: formatCurrency(selectedItem.UnitPrice),
                            },
                            {
                              label: "Purchase Cost",
                              value: formatCurrency(selectedItem.PurchaseCost),
                            },
                          ].map((row) => (
                            <div
                              key={row.label}
                              className="flex gap-x-3 gap-y-2 items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0"
                            >
                              <p className="text-sm text-gray-500 font-medium">
                                {row.label}
                              </p>
                              <p className="text-sm font-semibold text-gray-800">
                                {row.value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Record Info */}
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-widest">
                          Record Info
                        </p>
                        <div className="pt-2 grid grid-cols-2 gap-3">
                          {[
                            {
                              label: "Created On",
                              value: formatDate(
                                selectedItem.MetaData?.CreateTime,
                              ),
                            },
                            {
                              label: "Last Updated",
                              value: formatDate(
                                selectedItem.MetaData?.LastUpdatedTime,
                              ),
                            },
                          ].map((d) => (
                            <div
                              key={d.label}
                              className="p-3 border border-gray-100 rounded-xl bg-gray-50/50"
                            >
                              <p className="text-[11px] text-gray-600 font-medium uppercase tracking-wide">
                                {d.label}
                              </p>
                              <p className="text-sm text-gray-800 mt-0.5 break-words">
                                {d.value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Modal Footer */}
                  <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="inline-flex items-center gap-2 text-sm text-gray-600 hover:opacity-80 rounded-xl transition-colors border border-gray-200 bg-white font-medium px-5 py-2.5"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
}
